import axios from 'axios';
import apiClient from '../utils/api/ApiClient';
import { API_ENDPOINTS } from '../constants/api.constants';
import type {
    BlogListParams,
    BlogListResponse,
    BlogDetail,
    BlogSortingFiltersResponse,
    BlogImageUploadUrlRequest,
    BlogImageUploadUrlResponse,
    CreateBlogRequest,
    UpdateBlogRequest,
} from '../types/api.types';

export const getBlogSortingFilters = async (): Promise<BlogSortingFiltersResponse> => {
    const response = await apiClient.get<{ data: BlogSortingFiltersResponse }>(
        API_ENDPOINTS.BLOG.SORTING_FILTERS
    );
    return response.data;
};

export const getBlogList = async (params: BlogListParams): Promise<BlogListResponse> => {
    const response = await apiClient.post<{ data: BlogListResponse }>(
        API_ENDPOINTS.BLOG.LIST,
        params
    );
    return response.data;
};

export const getBlogDetails = async (blogId: string): Promise<BlogDetail> => {
    const response = await apiClient.post<{ data: BlogDetail }>(API_ENDPOINTS.BLOG.DETAILS, {
        _id: blogId,
    });
    return response.data;
};

export const getBlogImageUploadUrl = async (
    req: BlogImageUploadUrlRequest
): Promise<BlogImageUploadUrlResponse> => {
    const response = await apiClient.post<{ data: BlogImageUploadUrlResponse }>(
        API_ENDPOINTS.BLOG.IMAGE_UPLOAD_URL,
        req
    );
    return response.data;
};

/**
 * Upload a file directly to S3 using a presigned URL.
 * IMPORTANT: Must use plain axios (not apiClient) — the presigned URL
 * already encodes auth. Adding a Bearer token header breaks the S3 signature.
 */
export const uploadFileToS3 = async (
    uploadUrl: string,
    file: File,
    fileType: string
): Promise<void> => {
    await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': fileType },
    });
};

export const createBlog = async (req: CreateBlogRequest): Promise<{ _id: string; slug: string }> => {
    const response = await apiClient.post<{ data: { _id: string; slug: string } }>(
        API_ENDPOINTS.BLOG.CREATE,
        req
    );
    return response.data;
};

export const updateBlog = async (req: UpdateBlogRequest): Promise<void> => {
    await apiClient.patch(API_ENDPOINTS.BLOG.UPDATE, req);
};

export const toggleBlogStatus = async (blogId: string): Promise<{ status: string }> => {
    const response = await apiClient.patch<{ data: { status: string } }>(
        API_ENDPOINTS.BLOG.STATUS,
        { _id: blogId }
    );
    return response.data;
};

export const deleteBlog = async (blogId: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.BLOG.DELETE, { data: { _id: blogId } });
};
